import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { fillForm } from '../lib/form.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { confirmAction, toastError, toastSuccess } from '../lib/toast.js';

/**
 * The Question of the day page.
 *
 * The schedule is a form with a save bar; the reserve is not. Adding, approving
 * and deleting take effect the moment they are clicked, and putting them behind
 * "Save" would suggest they could be taken back — the same choice the Reports
 * page makes for unblocking.
 *
 * Each of the three reloads the page rather than patching the list in place:
 * the quota line above the list counts what is waiting, and a reserve that
 * disagrees with its own counter is worse than a page that blinks.
 */

interface Settings {
  channelId: string;
  mentionRoleId: string;
  hour: number;
  minute: number;
  requireApproval: boolean;
  maxPerMember: number;
  openThread: boolean;
}

export function initQotdPage(): void {
  const form = $<HTMLFormElement>('[data-qotd-form]');
  if (!form) return;

  const { settings, guildId } = pageData<{ settings: Settings; guildId: string }>();

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/qotd?guildId=${guildId}`, {
        guildId,
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        mentionRoleId: findSelectMenu(form, 'mentionRoleId')?.single ?? '',
        hour: number(form, 'hour'),
        minute: number(form, 'minute'),
        requireApproval: checkbox(form, 'requireApproval'),
        maxPerMember: number(form, 'maxPerMember'),
        openThread: checkbox(form, 'openThread'),
      });
    },
  });

  fillForm(form, settings as unknown as Record<string, unknown>);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = settings.channelId ? [settings.channelId] : [];

  const role = findSelectMenu(form, 'mentionRoleId');
  if (role) role.value = settings.mentionRoleId ? [settings.mentionRoleId] : [];

  wireReserve(guildId);
}

function wireReserve(guildId: string): void {
  const input = $<HTMLInputElement>('[data-qotd-new]');
  const add = $<HTMLButtonElement>('[data-qotd-add]');

  if (input && add) {
    const submit = (): void => {
      const text = input.value.trim();
      if (!text) return;

      // Disabled while it flies: a double click would spend two of the quota on
      // one question, and the second would come back as a duplicate.
      add.disabled = true;

      /*
       * The refusal is shown, not swallowed.
       *
       * Incident, 19 September 2026: adding a question the reserve already held
       * answered 500, nothing caught it, and the button simply came back to
       * life — so the same question was sent five times. A refusal the
       * interface hides is a refusal the visitor repeats.
       */
      void api
        .post(`/api/qotd/questions?guildId=${guildId}`, { guildId, text })
        .then(() => {
          toastSuccess(ct('questionAdded', 'Question added.'));
          location.reload();
        })
        .catch(toastError)
        .finally(() => {
          add.disabled = false;
        });
    };

    add.addEventListener('click', submit);

    // Enter submits: a field with a button beside it that ignores the keyboard
    // is a field people type into twice.
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      submit();
    });
  }

  for (const item of $$<HTMLElement>('[data-qotd-item]')) {
    const id = item.dataset.qotdItem ?? '';
    if (!id) continue;

    $<HTMLButtonElement>('[data-qotd-approve]', item)?.addEventListener('click', () => {
      void api
        .patch(`/api/qotd/questions/${id}?guildId=${guildId}`, { guildId })
        .then(() => {
          toastSuccess(ct('questionApproved', 'Question approved.'));
          location.reload();
        })
        .catch(toastError);
    });

    $<HTMLButtonElement>('[data-qotd-delete]', item)?.addEventListener('click', () => {
      void confirmAction(ct('confirmDeleteQuestion', 'Delete this question?')).then((confirmed) => {
        if (!confirmed) return;

        void api
          .delete(`/api/qotd/questions/${id}?guildId=${guildId}`)
          .then(() => {
            toastSuccess(ct('questionDeleted', 'Question deleted.'));
            location.reload();
          })
          .catch(toastError);
      });
    });
  }
}

function number(form: HTMLFormElement, name: string): number {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? Number(field.value) || 0 : 0;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
